// FILE: src/components/questions/QuestionExhibit.js
//
// Renders an optional image exhibit above a question stem.  Used by the
// "visual_interpretation" question type added in Phase 3 (migration 013).
//
// ── Props ───────────────────────────────────────────────────────────────────
//
//   image   { src: string, alt?: string } | null
//
// ── Behaviour ───────────────────────────────────────────────────────────────
//
//   • Returns null when image is missing or has no src — the caller doesn't
//     need to gate the render itself.
//   • The <img> sets max-width: 100% so it never overflows the card.
//   • A subtle border + neutral surface gives the exhibit visual separation
//     from the question stem and the choices.  Colors use semantic CSS
//     variables (with safe rgba fallbacks) so the component plays nicely
//     with future high-contrast / themed modes without a refactor.
//   • If the image fails to load (404 / CORS), an inline fallback message
//     replaces the broken-image icon.  The error state is local; it does
//     not throw and does not prevent the rest of the question from
//     rendering.
//   • Heights are capped (~440px desktop, ~280px mobile) so a tall chart
//     doesn't push the choices below the fold.

import React, { useState } from 'react';
import css from './QuestionExhibit.module.css';

const styles = {
  wrapper: {
    display: 'flex',
    justifyContent: 'center',
    margin: '8px 0 20px',
    padding: '12px',
    background: 'var(--exhibit-bg, rgba(0, 0, 0, 0.02))',
    border: '1px solid var(--exhibit-border, rgba(0, 0, 0, 0.12))',
    borderRadius: '6px',
    // Allow horizontal scroll on very narrow viewports for ultra-wide images
    // rather than cropping or squeezing them.
    overflow: 'auto',
  },
  image: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: '440px',
    height: 'auto',
    objectFit: 'contain',
    // The image itself sets no background — the wrapper provides one.
  },
  fallback: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '120px',
    padding: '12px',
    color: 'var(--exhibit-fallback-color, #777)',
    fontSize: '0.85rem',
    fontStyle: 'italic',
    textAlign: 'center',
  },
};

/**
 * Renders an exhibit image for a visual_interpretation question.  Returns
 * null when no usable image is provided so callers can render unconditionally.
 *
 * @param {{ image: ?{src: string, alt?: string} }} props
 * @returns {JSX.Element|null}
 */
export default function QuestionExhibit({ image }) {
  const [errored, setErrored] = useState(false);

  // Defensive: render nothing when the prop is missing or unusable.
  if (!image || typeof image !== 'object' || !image.src) {
    return null;
  }

  // alt text is recommended but not required.  When absent we use a generic
  // string so screen readers don't read out the URL.
  const altText = (typeof image.alt === 'string' && image.alt.trim())
    ? image.alt
    : 'Question exhibit';

  if (errored) {
    return (
      <div
        data-testid="question-exhibit"
        data-state="errored"
        style={styles.wrapper}
        className={css.wrapper}
      >
        <div style={styles.fallback} className={css.fallback}>
          (Exhibit unavailable — could not load image)
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="question-exhibit"
      data-state="loaded"
      style={styles.wrapper}
      className={css.wrapper}
    >
      <img
        data-testid="question-exhibit-image"
        src={image.src}
        alt={altText}
        style={styles.image}
        className={css.image}
        onError={() => setErrored(true)}
        // Eager-load: exhibit is critical to answering the question.
        loading="eager"
      />
    </div>
  );
}
